import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Input from "../../../../componentes/input/input";
import Textarea from "../../../../componentes/input/textarea";
import Button from "../../../../componentes/button/button";
import { CustomDropdown } from "../../../../componentes/dropdown/CustomDropdown";
import FilialService from "../../../../services/resource/filialService";
import NovaSolicitacaoLayout from "../shared/NovaSolicitacaoLayout";
import { useRascunhoFormulario } from "../../Rascunho/RascunhoContext";
import { listaDoRascunho, mesclarComPadrao } from "../../../../utils/solicitacoes/rascunhoStorage";
import { enviarAnexosStaged } from "../../../../utils/solicitacoes/anexosStaged";
import { enviarComentariosStaged } from "../../../../utils/solicitacoes/comentariosStaged";
import { SETOR_OPTIONS } from "../../../../utils/solicitacoes/setores";
import { useCargoGerencial } from "../../../../hooks/solicitacoes/useCargoGerencial";

const filialService = new FilialService();

const MOTIVO_VAGA_OPTIONS = [
    { value: "AUMENTO_QUADRO", label: "Aumento de Quadro" },
    { value: "SUBSTITUICAO", label: "Substituição" },
];

const SIM_NAO_OPTIONS = [
    { value: true, label: "Sim" },
    { value: false, label: "Não" },
];

const ADICIONAIS_OPTIONS = ["Insalubridade", "Periculosidade", "Adicional Noturno", "Adicional de Função"];

const inicial = {
    unidadeId: null,
    motivoVaga: null,
    vagaSigilosa: null,
    motivoSolicitacao: "",
    cargoAPreencher: "",
    horario: "",
    adicionais: [],
    setor: "",
    cidadeTrabalho: "",
    resumoAtribuicoes: "",
};

function CheckboxAdicional({ label, checked, onChange }) {
    return (
        <label className="flex items-center gap-2.5 px-3 py-2 border border-input rounded-md text-sm font-medium text-foreground/80 cursor-pointer hover:bg-muted">
            <input
                type="checkbox"
                checked={checked}
                onChange={e => onChange(e.target.checked)}
                className="h-4 w-4 rounded border-input text-primary focus:ring-2 focus:ring-primary/15"
            />
            {label}
        </label>
    );
}

export default function NovaSolicitacaoPessoalForm({ service, etapaInicialExigeAnexo, onCriada, encaminhar }) {
    const [form, setForm] = useState(inicial);
    const [anexos, setAnexos] = useState([]);
    const [comentarios, setComentarios] = useState([]);

    // Rascunho (botão "Salvar rascunho" da página) — só campos, anexos ficam de fora.
    useRascunhoFormulario(
        () => ({ form, comentarios }),
        dados => {
            setForm(mesclarComPadrao(inicial, dados.form));
            setComentarios(listaDoRascunho(dados.comentarios));
        },
    );
    const [unidades, setUnidades] = useState([]);
    const [enviando, setEnviando] = useState(false);
    const { carregando: carregandoCargo, autorizado: autorizadoPorCargo, filial: filialSolicitante } = useCargoGerencial();

    useEffect(() => {
        filialService.listarAtivas().then(res => setUnidades(res.data)).catch(() => setUnidades([]));
    }, []);

    // Pré-preenche a filial com a do usuário logado assim que a consulta ao perfil retornar (a
    // busca é assíncrona, então isso não pode ficar num efeito com array de dependências vazio —
    // ele rodaria antes do valor chegar). Só preenche se o campo ainda estiver vazio.
    useEffect(() => {
        if (!filialSolicitante) return;
        setForm(prev => (prev.unidadeId ? prev : { ...prev, unidadeId: filialSolicitante }));
    }, [filialSolicitante]);

    function set(campo, valor) {
        setForm(prev => ({ ...prev, [campo]: valor }));
    }

    function alternarAdicional(adicional, marcado) {
        setForm(prev => ({
            ...prev,
            adicionais: marcado ? [...prev.adicionais, adicional] : prev.adicionais.filter(a => a !== adicional),
        }));
    }

    function validar() {
        if (!form.unidadeId || !form.motivoVaga || form.vagaSigilosa === null) {
            toast.error("Preencha todos os campos obrigatórios.");
            return false;
        }
        if (!form.motivoSolicitacao.trim() || !form.cargoAPreencher.trim() || !form.horario.trim()
            || !form.setor.trim() || !form.cidadeTrabalho.trim() || !form.resumoAtribuicoes.trim()) {
            toast.error("Preencha todos os campos obrigatórios.");
            return false;
        }
        if (etapaInicialExigeAnexo && anexos.length === 0) {
            toast.error("É necessário anexar pelo menos um documento para concluir esta etapa.");
            return false;
        }
        return true;
    }

    async function enviar() {
        if (!validar()) return;

        let usuarioEscolhidoId;
        try {
            usuarioEscolhidoId = await encaminhar("ETAPA_2_APROVACAO_DIRETORIA");
        } catch {
            return;
        }

        setEnviando(true);
        service.criarSolicitacao({ ...form, usuarioEscolhidoId })
            .then(async res => {
                const [{ falha: falhaAnexos }, { falha: falhaComentarios }] = await Promise.all([
                    enviarAnexosStaged(res.data.id, anexos),
                    enviarComentariosStaged(res.data.id, comentarios),
                ]);
                if (falhaAnexos.length > 0 || falhaComentarios.length > 0) {
                    const partes = [];
                    if (falhaAnexos.length > 0) partes.push(`${falhaAnexos.length} anexo(s) (${falhaAnexos.map(f => `${f.arquivo.name}: ${f.erro}`).join(", ")})`);
                    if (falhaComentarios.length > 0) partes.push(`${falhaComentarios.length} comentário(s)`);
                    toast.error(`Solicitação criada, mas houve falha ao enviar: ${partes.join(" e ")}.`);
                }
                onCriada(res.data);
            })
            .catch(error => console.error("Erro ao criar solicitação de pessoal", error))
            .finally(() => setEnviando(false));
    }

    if (carregandoCargo) {
        return (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                <i className="fa fa-spinner fa-spin mr-2" /> Verificando permissão...
            </div>
        );
    }

    if (!autorizadoPorCargo) {
        return (
            <div className="flex flex-col items-center gap-2 py-16 text-center">
                <i className="fa fa-lock text-muted-foreground text-xl" />
                <p className="text-sm text-foreground font-medium">Você não tem permissão para iniciar esta solicitação.</p>
                <p className="text-xs text-muted-foreground">Apenas Supervisores, Coordenadores ou Gerentes podem abrir este processo.</p>
            </div>
        );
    }

    return (
        <NovaSolicitacaoLayout
            anexos={anexos}
            onAnexosChange={setAnexos}
            comentarios={comentarios}
            onComentariosChange={setComentarios}
            etapaInicialExigeAnexo={etapaInicialExigeAnexo}
        >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                    <label className="text-sm font-medium text-foreground/80 block mb-1.5">Filial <span className="text-destructive">*</span></label>
                    <CustomDropdown value={form.unidadeId} onChange={e => set("unidadeId", e.value)} options={unidades}
                        optionValue="id" optionLabel="nome" placeholder="Selecione" filter />
                </div>

                <div>
                    <label className="text-sm font-medium text-foreground/80 block mb-1.5">Motivo <span className="text-destructive">*</span></label>
                    <CustomDropdown value={form.motivoVaga} onChange={e => set("motivoVaga", e.value)} options={MOTIVO_VAGA_OPTIONS} placeholder="Selecione" />
                </div>

                <div>
                    <label className="text-sm font-medium text-foreground/80 block mb-1.5">Vaga sigilosa <span className="text-destructive">*</span></label>
                    <CustomDropdown value={form.vagaSigilosa} onChange={e => set("vagaSigilosa", e.value)} options={SIM_NAO_OPTIONS} placeholder="Selecione" />
                </div>
            </div>

            <Textarea label="Motivo da solicitação" required value={form.motivoSolicitacao} onChange={e => set("motivoSolicitacao", e.target.value)} rows={3} />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Cargo a preencher" required value={form.cargoAPreencher} onChange={e => set("cargoAPreencher", e.target.value)} />
                <Input label="Horário" required placeholder="Ex: 08h às 18h, escala 6x1..." value={form.horario} onChange={e => set("horario", e.target.value)} />
            </div>

            <div>
                <label className="text-sm font-medium text-foreground/80 block mb-1.5">Adicionais aplicáveis</label>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                    {ADICIONAIS_OPTIONS.map(adicional => (
                        <CheckboxAdicional
                            key={adicional}
                            label={adicional}
                            checked={form.adicionais.includes(adicional)}
                            onChange={marcado => alternarAdicional(adicional, marcado)}
                        />
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <CustomDropdown label="Setor" required value={form.setor} onChange={e => set("setor", e.value)} options={SETOR_OPTIONS} placeholder="Selecione" />
                <Input label="Cidade de trabalho" required value={form.cidadeTrabalho} onChange={e => set("cidadeTrabalho", e.target.value)} />
            </div>

            <Textarea label="Resumo das atribuições" required value={form.resumoAtribuicoes} onChange={e => set("resumoAtribuicoes", e.target.value)} rows={4} />

            <div className="flex justify-end">
                <Button variant="blue" label="Solicitar" iconLeft="fa fa-paper-plane" loading={enviando} onClick={enviar} />
            </div>
        </NovaSolicitacaoLayout>
    );
}
